import { baseApi } from '@/features/api/baseApi';
import directus, { type DirectusErrorType, type RequestQuery } from '@/features/api/directus';
import type { DirectusFiles } from '@/models/types';
import {
  readFile,
  readFiles,
  deleteFile as sdkDeleteFile,
  deleteFiles as sdkDeleteFiles,
  updateFile,
} from '@directus/sdk';
import type { QueryReturnValue } from '@reduxjs/toolkit/query';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type Result<T> = QueryReturnValue<T, DirectusErrorType, {}>;

const getFilesFn = async (
  query: RequestQuery<DirectusFiles> | undefined,
): Promise<Result<DirectusFiles[]>> => {
  try {
    const data = await directus.request<DirectusFiles[]>(readFiles(query as never));
    return { data };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const getFileFn = async (id: string): Promise<Result<DirectusFiles>> => {
  try {
    const data = await directus.request<DirectusFiles>(readFile(id));
    return { data };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

// Envoi via l'extension Directus oru-ep-antivirus : le fichier est analysé par ClamAV avant stockage
// (422 VIRUS_DETECTED si infecté, 503 si l'analyse est indisponible).
const uploadFileFn = async (formData: FormData): Promise<Result<DirectusFiles>> => {
  try {
    const data = await directus.request<DirectusFiles>(() => ({
      path: '/antivirus/upload',
      method: 'POST',
      body: formData,
      // Le SDK retire cet en-tête pour laisser le navigateur ajouter la boundary multipart
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
    return { data };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const updateFileFn = async ({
  id,
  body,
}: {
  id: string;
  body: Partial<DirectusFiles>;
}): Promise<Result<DirectusFiles>> => {
  try {
    const data = await directus.request<DirectusFiles>(updateFile(id, body as never));
    return { data };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const deleteFileFn = async (id: string): Promise<Result<void>> => {
  try {
    await directus.request<void>(sdkDeleteFile(id));
    return { data: undefined };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const deleteFilesFn = async (ids: string[]): Promise<Result<void>> => {
  try {
    await directus.request<void>(sdkDeleteFiles(ids));
    return { data: undefined };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const fileApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['file', 'files'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getFiles: builder.query<DirectusFiles[], RequestQuery<DirectusFiles> | undefined>({
        queryFn: getFilesFn,
        providesTags: ['files'],
      }),
      getFile: builder.query<DirectusFiles, string>({
        queryFn: getFileFn,
        providesTags: ['file'],
      }),
      uploadFile: builder.mutation<DirectusFiles, FormData>({
        queryFn: uploadFileFn,
        invalidatesTags: ['files'],
      }),
      updateFile: builder.mutation<DirectusFiles, { id: string; body: Partial<DirectusFiles> }>({
        queryFn: updateFileFn,
        invalidatesTags: ['file', 'files'],
      }),
      deleteFile: builder.mutation<void, string>({
        queryFn: deleteFileFn,
        invalidatesTags: ['file', 'files'],
      }),
      deleteFiles: builder.mutation<void, string[]>({
        queryFn: deleteFilesFn,
        invalidatesTags: ['file', 'files'],
      }),
    }),
  });

export const {
  useGetFilesQuery,
  useGetFileQuery,
  useUploadFileMutation,
  useUpdateFileMutation,
  useDeleteFileMutation,
  useDeleteFilesMutation,
} = fileApi;
